import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import UpsellQuery from './UpsellQuery.gql';
import { styled } from 'linaria/react';
import Image from '@jetshop/ui/Image';
import ProductLink from '@jetshop/ui/ProductLink';
import { VariantSelector } from './VariantSelector';
import { useProductVariants } from '@jetshop/core/hooks/useProductVariants';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import AddToCartButton from './AddToCart/AddToCartButton';
import AddToCartWrapper from './AddToCart/AddToCartWrapper';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { theme } from '../Theme';
import { PriceComponent } from './PriceComponent';

const UpsellWrapper = styled('div')`
  width: 100%;
  background: rgba(251, 248, 244, 0.6);
  padding: 50px 0;
  h3 {
    text-align: center;
    margin-top: 0;
    margin-bottom: 30px;
    color: #000;
    text-align: center;
    font-family: ${theme.fonts.primary};
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: 114%; /* 27.36px */
  }
`;

const UpsellProductsWrapper = styled('div')`
  display: flex;
  margin: auto;
  justify-content: center;
  gap: 20px;

  flex-wrap: nowrap;
  overflow: scroll;
  padding: 0 14px;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  ${theme.below.lg} {
    gap: 14px;
    justify-content: flex-start;
    max-width: 100%;
    margin: 0;
  }
`;

const NewUpsellProductWrapper = styled('div')`
  width: 100%;
  min-width: 230px;
  max-width: 230px;

  form {
    margin: 0;
  }
  button {
    height: 28px;
    border-radius: 0;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.55px;
    text-transform: uppercase;
    border-radius: 0px 0px 10px 10px;
  }
  input {
    display: none;
  }

  h4 {
    color: #000;
    font-family: ${theme.fonts.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    margin-top: 14px;
    margin-bottom: 0;
  }

  .variant-selector {
    margin-top: 10px;
    h3 {
      display: none;
    }
  }
  .big {
    width: 24px;
    height: 24px;
  }

  .description {
    margin: 10px 0;
    margin-right: 4px;

    p {
      color: #000;
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px; /* 153.846% */
      margin-bottom: 0;
    }
    span {
      cursor: pointer;
      text-decoration: underline;
      margin-left: 8px;
    }
  }

  .button-container {
    margin: 0 0 20px 0px;
  }

  [data-flight-price] {
    display: flex;
  }

  .price,
  .new-price,
  .old-price {
    font-size: 16px;
  }
  .old-price {
    text-decoration: line-through;
    margin-left: 10px;
  }

  .mastarpris {
    .price {
      font-size: 16px;
    }
  }

  .stock-status-indicator {
    font-size: 11px !important;
    margin-top: 10px !important;
  }

  .color-selection {
    max-width: 230px;
    gap: 0.5rem;

    .big {
      width: 18px;
      height: 18px;
    }
  }
`;

const ImageWrapper = styled(ProductLink)`
  width: 100%;
  :hover {
    opacity: 0.8 !important;
  }
  img {
    border: 1px solid #e5e5e5;
  }
`;

const NewUpsellProduct = ({ upsellProduct, storeName }) => {
  const variantHandler = useProductVariants(
    upsellProduct?.articleNumber ? upsellProduct : undefined
  );
  const { selectedVariant: selectedVariation } = variantHandler;
  const stockStatus = useStockStatus(selectedVariation || upsellProduct);
  const [activeImage, setActiveImage] = useState(upsellProduct?.images[0]?.url);
  const imageSizes = [1 / 2, 1 / 2, 1 / 3, 1 / 4];
  const short = upsellProduct?.shortDescription?.substring(0, 100);
  const long = upsellProduct?.shortDescription;
  const [showDescription, setShowDescription] = useState(short);

  useEffect(() => {
    setActiveImage(selectedVariation?.images[0]?.url);
  }, [selectedVariation]);

  return (
    <NewUpsellProductWrapper>
      <ImageWrapper product={upsellProduct}>
        {upsellProduct && (
          <Image
            sizes={imageSizes}
            aspect={'1:1'}
            alt={upsellProduct?.images[0]?.alt}
            src={activeImage ? activeImage : upsellProduct?.images[0]?.url}
          />
        )}
      </ImageWrapper>
      <AddToCartWrapper product={upsellProduct} variantHandler={variantHandler}>
        {({ errors }) => (
          <AddToCartButton
            inStock={stockStatus?.status === 'inStock'}
            variantHandler={variantHandler}
            errors={errors}
          />
        )}
      </AddToCartWrapper>
      <h4 product={upsellProduct}>{upsellProduct?.name}</h4>
      <div className="description">
        <p
          onClick={() =>
            setShowDescription(showDescription === short ? long : short)
          }
          dangerouslySetInnerHTML={{
            __html:
              showDescription +
              (showDescription === short
                ? '...<span>Läs mer</span>'
                : '<span>Visa mindre</span>'),
          }}
        />
      </div>
      {upsellProduct?.hasVariants && (
        <VariantSelector
          product={upsellProduct}
          selectedVariation={selectedVariation}
          variantHandler={variantHandler}
        />
      )}
      <PriceComponent product={upsellProduct} />
      {stockStatus?.text.includes('För att se') ? null : (
        <StockStatusIndicator
          status={stockStatus?.status}
          text={stockStatus?.text}
          storeName={storeName}
        />
      )}
    </NewUpsellProductWrapper>
  );
};

// Component to fetch a single product
const SingleProductFetcher = ({ articleNumber, onSuccess, onError }) => {
  const { data, loading, error } = useQuery(UpsellQuery, {
    variables: {
      articles: [articleNumber], // Query just this one product
    },
  });

  useEffect(() => {
    if (!loading) {
      if (error || !data?.products?.[0]) {
        console.warn(`Failed to load product ${articleNumber}:`, error);
        onError?.(articleNumber);
      } else {
        onSuccess?.(data.products[0]);
      }
    }
  }, [data, loading, error, articleNumber, onSuccess, onError]);

  return null; // This component doesn't render anything
};

export const UpsellNew = ({ productIds }) => {
  // Always call useQuery at the top, but skip execution if no products
  const { data, loading, error } = useQuery(UpsellQuery, {
    variables: {
      articles: productIds,
    },
    errorPolicy: 'all',
  });

  // Early returns after all hooks have been called

  if (loading || error) return null;

  // Filter out null, undefined, or incomplete products
  const validProducts = data?.products?.filter((item) => {
    const isValid =
      item &&
      item.articleNumber &&
      item.name &&
      item.images &&
      item.images.length > 0;

    if (!isValid) {
      console.info('Filtering out invalid product:', item);
    }

    return isValid;
  });

  // If no valid products, don't render
  if (validProducts?.length === 0) {
    console.info('No valid products found, not rendering component');
    return null;
  }

  return (
    <UpsellWrapper className="hideInPrint">
      <h3>Komplettera med</h3>
      <UpsellProductsWrapper>
        {validProducts?.map((item) => {
          return (
            <NewUpsellProduct upsellProduct={item} key={item?.articleNumber} />
          );
        })}
      </UpsellProductsWrapper>
    </UpsellWrapper>
  );
};
